import { useId } from "react";
import type { BlockBackground } from "@/types";
import { BACKGROUND_COLOR_TOKENS, DEFAULT_OVERLAY, resolveColor } from "@/renderer/background";
import { ImageUploader } from "./ImageUploader";

type Kind = "none" | "color" | "image";

/** Shared Background control (§5A) — one consistent expandable section reused in
 *  every block editor. Edits the block's optional `background` modifier. */
export function BackgroundControl({
  background,
  onChange,
}: {
  background?: BlockBackground;
  onChange: (bg: BlockBackground | undefined) => void;
}) {
  const kind: Kind = background?.kind ?? "none";
  const groupName = useId();

  function setKind(next: Kind) {
    if (next === "none") return onChange(undefined);
    if (next === "color") return onChange({ kind: "color", color: background?.color ?? "slate" });
    return onChange({
      kind: "image",
      asset_url: background?.asset_url ?? "",
      overlay: background?.overlay ?? DEFAULT_OVERLAY,
    });
  }

  return (
    <details className="bg-control">
      <summary className="bg-control__summary">
        Background{kind !== "none" ? ` · ${kind}` : ""}
      </summary>

      <div className="bg-control__body">
        <div className="bg-control__kinds" role="radiogroup" aria-label="Background type">
          {(["none", "color", "image"] as Kind[]).map((k) => (
            <label key={k} className={`bg-control__kind${kind === k ? " is-active" : ""}`}>
              <input
                type="radio"
                name={groupName}
                checked={kind === k}
                onChange={() => setKind(k)}
              />
              {k === "none" ? "None" : k === "color" ? "Colour" : "Image"}
            </label>
          ))}
        </div>

        {kind === "color" ? (
          <div className="bg-control__swatches">
            {BACKGROUND_COLOR_TOKENS.map((t) => (
              <button
                key={t.name}
                type="button"
                title={t.label}
                className={`bg-control__swatch${background?.color === t.name ? " is-active" : ""}`}
                style={{ background: resolveColor(t.name) }}
                onClick={() => onChange({ kind: "color", color: t.name })}
              />
            ))}
          </div>
        ) : null}

        {kind === "image" ? (
          <div className="bg-control__image">
            <ImageUploader
              label="Background image"
              value={background?.asset_url}
              onChange={(url) =>
                onChange({ kind: "image", asset_url: url, overlay: background?.overlay ?? DEFAULT_OVERLAY })
              }
            />
            <label className="bg-control__overlay">
              <span>Overlay darkness: {background?.overlay ?? DEFAULT_OVERLAY}%</span>
              <input
                type="range"
                min={0}
                max={100}
                value={background?.overlay ?? DEFAULT_OVERLAY}
                onChange={(e) =>
                  onChange({
                    kind: "image",
                    asset_url: background?.asset_url ?? "",
                    overlay: Number(e.target.value),
                  })
                }
              />
            </label>
          </div>
        ) : null}
      </div>
    </details>
  );
}
